/**
 * useUnreadCounts Hook
 * 
 * Centralized hook for managing all unread counts (messages, notifications, chat).
 * Follows the same pattern as Header component for consistency.
 * 
 * @author Andres Nava
 * @version 1.0.0
 */

import { useState, useEffect, useRef } from 'react';
import { isSessionAuthenticatedAsync } from '../utils/storageUtils';
import { safeAPICall } from '../utils/trackingUtils';
import { logger } from '../utils/logger';

interface UnreadCounts {
  messages: number;
  notifications: number;
  chat: number;
}

interface UseUnreadCountsResult {
  counts: UnreadCounts;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and manage unread counts for messages, notifications, and chat
 * @param isAuthenticated - Whether user is authenticated
 * @param userId - User ID (optional, will be fetched from session if not provided)
 * @param enabled - Whether to enable fetching (default: true)
 */
export const useUnreadCounts = (
  isAuthenticated: boolean,
  userId?: number,
  enabled: boolean = true
): UseUnreadCountsResult => {
  const [counts, setCounts] = useState<UnreadCounts>({
    messages: 0,
    notifications: 0,
    chat: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs to prevent duplicate requests and manage cleanup
  const hasFetchedCountsRef = useRef<boolean>(false);
  const fetchCountsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const notificationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch unread message count
  const fetchUnreadMessageCount = async (): Promise<number> => {
    if (!isAuthenticated) {
      return 0;
    }

    try {
      const isAuth = await isSessionAuthenticatedAsync();
      if (!isAuth) {
        return 0;
      }

      const result = await safeAPICall(
        async () => {
          const response = await fetch('/api/messaging/messages/unread-count', {
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            signal: AbortSignal.timeout(5000),
          });

          if (response.ok) {
            const data = await response.json();
            return data.count || 0;
          } else if (response.status === 401 || response.status === 403) {
            return 0;
          } else if (response.status >= 500) {
            logger.error('Server error fetching unread message count, setting to 0', undefined, {});
            return 0;
          } else {
            return 0;
          }
        },
        0, // fallback value
        'Unread Messages'
      );

      return result;
    } catch (error) {
      // Silently handle errors to prevent console spam
      if (import.meta.env.DEV) {
        logger.debug('Error fetching unread message count', { error });
      }
      return 0;
    }
  };

  // Fetch unread notification count
  const fetchUnreadNotificationCount = async (): Promise<number> => {
    if (!isAuthenticated) {
      return 0;
    }

    try {
      const isAuth = await isSessionAuthenticatedAsync();
      if (!isAuth) {
        return 0;
      }

      const result = await safeAPICall(
        async () => {
          const response = await fetch('/api/notifications/unified?limit=1', {
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            signal: AbortSignal.timeout(5000),
          });

          if (response.ok) {
            const data = await response.json();
            // Return just notifications count (not combined with messages)
            return data.data?.counts?.unreadNotifications || 0;
          } else if (response.status === 401 || response.status === 403) {
            return 0;
          } else if (response.status >= 500) {
            logger.error('Server error fetching unread notification count, setting to 0', undefined, {});
            return 0;
          } else {
            return 0;
          }
        },
        0, // fallback value
        'Unread Notifications'
      );

      return result;
    } catch (error) {
      // Silently handle errors to prevent console spam
      if (import.meta.env.DEV) {
        logger.debug('Error fetching unread notification count', { error });
      }
      return 0;
    }
  };

  // Fetch all counts
  const fetchAllCounts = async () => {
    if (!isAuthenticated || !enabled) {
      setCounts({ messages: 0, notifications: 0, chat: 0 });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Validate session before making API calls
      const isAuth = await isSessionAuthenticatedAsync();
      if (!isAuth) {
        setCounts({ messages: 0, notifications: 0, chat: 0 });
        setIsLoading(false);
        return;
      }

      // Stagger the calls slightly to avoid rate limiting
      const messagesCount = await fetchUnreadMessageCount();
      setTimeout(async () => {
        const notificationsCount = await fetchUnreadNotificationCount();
        // Chat count is same as messages count for now
        // In future, can add separate endpoint if needed
        setCounts({
          messages: messagesCount,
          notifications: notificationsCount, // Already just notifications from unified endpoint
          chat: messagesCount, // Using messages count for chat badge
        });
        setIsLoading(false);
      }, 300);
    } catch (err) {
      logger.error('Error fetching unread counts', err, {});
      setError(err instanceof Error ? err.message : 'Failed to fetch counts');
      setCounts({ messages: 0, notifications: 0, chat: 0 });
      setIsLoading(false);
    }
  };

  // Effect to handle count fetching with proper cleanup
  useEffect(() => {
    // Only proceed if authentication is fully initialized and enabled
    if (isAuthenticated && enabled && !hasFetchedCountsRef.current) {
      // Clear any existing timeout (React Strict Mode protection)
      if (fetchCountsTimeoutRef.current) {
        clearTimeout(fetchCountsTimeoutRef.current);
      }

      // Initial fetch - use setTimeout to avoid blocking
      // Increased delay to 1500ms to allow session cookie propagation across subdomains
      fetchCountsTimeoutRef.current = setTimeout(async () => {
        hasFetchedCountsRef.current = true;
        await fetchAllCounts();
      }, 1500);

      // Set up polling with reduced frequency (5 minutes matching Header component)
      messageIntervalRef.current = setInterval(() => {
        fetchUnreadMessageCount().then((count) => {
          setCounts((prev) => ({ ...prev, messages: count, chat: count }));
        });
      }, 300000); // 5 minutes

      notificationIntervalRef.current = setInterval(() => {
        fetchUnreadNotificationCount().then((count) => {
          setCounts((prev) => {
            return { ...prev, notifications: count };
          });
        });
      }, 300000); // 5 minutes

      return () => {
        // Clear timeout and intervals on cleanup
        if (fetchCountsTimeoutRef.current) {
          clearTimeout(fetchCountsTimeoutRef.current);
          fetchCountsTimeoutRef.current = null;
        }
        if (messageIntervalRef.current) {
          clearInterval(messageIntervalRef.current);
          messageIntervalRef.current = null;
        }
        if (notificationIntervalRef.current) {
          clearInterval(notificationIntervalRef.current);
          notificationIntervalRef.current = null;
        }
      };
    } else if (!isAuthenticated || !enabled) {
      // Clear counts when user is not authenticated or disabled
      setCounts({ messages: 0, notifications: 0, chat: 0 });
      setIsLoading(false);
      hasFetchedCountsRef.current = false; // Reset flag when user logs out
    }
  }, [isAuthenticated, enabled]);

  return {
    counts,
    isLoading,
    error,
  };
};
